import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center px-6 py-10">
      <div className="w-full rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="EasyStay" className="h-9 w-9 rounded-xl object-cover" />
          <span className="font-display text-xl font-bold text-slate-900">Easy<span className="text-copper">Stay</span></span>
        </div>

        {!token ? (
          <div data-testid="reset-notoken-panel" className="mt-6 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-rose-500" />
            <h1 className="mt-4 font-display text-2xl font-bold">Missing reset token</h1>
            <p className="mt-2 text-sm text-slate-600">Open the link from your email to reset your password.</p>
            <Link to="/forgot-password" className="btn-outline mt-6 inline-block text-sm">Request a new link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done-panel" className="mt-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
            <h1 className="mt-4 font-display text-2xl font-bold">Password updated</h1>
            <p className="mt-2 text-sm text-slate-600">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-display text-3xl font-bold text-slate-900">Set a new password</h1>
            <p className="mt-1 text-sm text-slate-600">Choose a strong password — at least 8 characters.</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">New password</label>
                <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
                  <Lock className="h-4 w-4 text-slate-400" />
                  <input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-transparent py-3 text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Confirm</label>
                <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
                  <Lock className="h-4 w-4 text-slate-400" />
                  <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-transparent py-3 text-sm outline-none" />
                </div>
              </div>
              <button data-testid="reset-submit" type="submit" disabled={loading} className="btn-primary w-full text-sm">
                {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
